/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo } from 'react';
import * as Yup from 'yup';
import { MdDiscount } from 'react-icons/md';
import { Form, Formik } from 'formik';
import DiscountCodeKindButton from './DiscountCodeKindButton';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import DateTimeRangePicker from 'legacy/components/util/FormHelpers/DateTimeRangePicker';
import { useGetEvent } from '../../hooks';
import dayjs from 'dayjs';
import Field from 'legacy/common/components/wysiwyg-form/Field';
import { discountCode as discountCodeRegexp } from '../../../util/validations/regexp';

const TIME_FORMAT = 'h:mm A';

const DiscountCodeSchema = Yup.object().shape({
  code: Yup.string()
    .matches(discountCodeRegexp, 'Invalid code')
    .required('Code is required')
    .max(20, 'Code should not be more than 20 characters'),
  amount: Yup.number()
    .required('Discount amount is required')
    .min(0, 'Amount must be 0 or greater')
});

type DiscountCodeFormProps = {
  eventId: string;
  title?: string;
  initialValues?: any;
  errorMessage?: string;
  onClose: () => void;
  onDelete?: (id) => void;
  onSubmit: ({
    eventId,
    code,
    discount
  }: {
    id?: string;
    eventId: string;
    code: string;
    discount: { value: number; kind: string };
    startDate?: Date;
    endDate?: Date;
    maxUsage?: number;
    isActive: boolean;
  }) => void;
};

const DiscountCodeForm: React.FC<DiscountCodeFormProps> = ({
  eventId,
  errorMessage = '',
  initialValues,
  title,
  onClose,
  onSubmit,
  onDelete
}) => {
  const { data: event, isLoading } = useGetEvent(eventId);

  const maxEndDate = useMemo(() => {
    if (isLoading || !event) {
      return null;
    }

    const effectiveEndDate = new Date(event.startDate);
    effectiveEndDate.setDate(effectiveEndDate.getDate() + 1);

    return event.endDate ? event.endDate : effectiveEndDate.toISOString();
  }, [event, isLoading]);

  return (
    <Formik
      initialValues={{
        code: initialValues?.code || '',
        amount: initialValues?.discount.value || undefined,
        kind: initialValues?.discount.kind || undefined,
        startDate: initialValues?.startDate || undefined,
        endDate: initialValues?.endDate || undefined,
        startDateTime: initialValues?.startDate
          ? dayjs(initialValues?.startDate).format(TIME_FORMAT)
          : undefined,
        endDateTime: initialValues?.endDate
          ? dayjs(initialValues?.endDate).format(TIME_FORMAT)
          : undefined,
        maxUsage: initialValues?.maxUsage || undefined,
        isActive:
          typeof initialValues?.isActive === 'boolean'
            ? initialValues.isActive
            : true
      }}
      onSubmit={(values) => {
        onSubmit({
          id: initialValues?.id || undefined,
          eventId,
          code: values.code.toUpperCase(),
          discount: {
            value: values.amount,
            kind: typeof values.kind === 'string' ? values.kind : 'percentage'
          },
          startDate: values.startDate,
          endDate: values.endDate,
          maxUsage: values.maxUsage,
          isActive: values.isActive
        });
      }}
      validationSchema={DiscountCodeSchema}
    >
      {({ values, submitForm }) => (
        <Modal
          type={ModalType.SMALL}
          visible
          onClose={onClose}
          shouldStopModalClose={isLoading}
          title={title || 'New discount Code'}
          icon={<MdDiscount size={22} />}
          renderFooter={() => (
            <div className="w-full">
              <div className="text-red-500">{errorMessage}</div>
              <Button loading={isLoading} onClick={submitForm}>
                Save discount code
              </Button>

              {typeof onDelete === 'function' && (
                <div className="mt-4">
                  <Button
                    type={ButtonType.Danger}
                    loading={isLoading}
                    onClick={() => {
                      onDelete(initialValues?.id);
                    }}
                  >
                    Delete Discount Code
                  </Button>
                </div>
              )}
            </div>
          )}
        >
          <Form>
            <div className="p-4 pb-52 mt-4 flex flex-col justify-between">
              <p className="mt-2 mb-6 text-sm font-normal">
                Discount codes reward loyal guests and fill out your crowd. You
                can add as many as you like, set usage limits, and pause them
                when needed to manage demand.
              </p>

              <Field
                id="code"
                name="code"
                type="text"
                label="Code*"
                placeholder="eg: SUMMERJAM10"
                helper="Allowed characters are A-Z, 0-9, $, %, !"
              />

              <div className="flex items-center justify-center gap-4">
                <Field
                  id="kind"
                  name="kind"
                  label="Discount type*"
                  render={DiscountCodeKindButton}
                />

                <Field
                  id="amount"
                  name="amount"
                  type="number"
                  label="Discount amount*"
                  placeholder={values.kind === 'amount' ? 'eg: 15%' : 'eg: 15'}
                  disabled={isLoading}
                />
              </div>
              <Field
                id="maxUsage"
                name="maxUsage"
                type="number"
                label="Usage limit"
                placeholder="e.g. 100 times"
                disabled={isLoading}
              />

              <DateTimeRangePicker maxEndDate={maxEndDate} />
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default DiscountCodeForm;
